import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PageHeader from '../../common/components/PageHeader';
import DashboardLayout from '../../common/layouts/Dashboard';
import { addBreadcrumb, BreadcrumbType, editBreadcrumbs, ReplaceType } from '../../common/utils/editBreadcrums';
import { getPageTitle } from '../../common/utils/pageUtils';
import { PageProps } from '../../types/PageProps';
import TimeTrackerFilterForm from './TimeTrackerFilterForm';
import { useParams } from 'react-router-dom';
import ErrorPage from '../error/ErrorPage';
import { HttpStatusCode } from '../../common/utils/httpStatusCode';
import { canViewTimeTracker } from '../../common/utils/permissionUtils';
import { selectUser } from '../auth/authSlice';
import { useAppSelector } from '../../app/hooks';

export default function TimeTrackerPage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const currentUser = useAppSelector(selectUser);
  const { patientId } = useParams();
  const isAllPatient = patientId === 'all';
  const replacePath: ReplaceType[] = isAllPatient ? [] : [{ param: ':patientId', value: patientId! }];
  const newBreadcrumb: BreadcrumbType = {
    text: isAllPatient ? t('patient.patientList.title') : t('patient.patientDashboard.title'),
    link: isAllPatient ? `/dashboard/patients/` : `/dashboard/patient/:patientId`,
  };
  const backUrl = `/dashboard/${isAllPatient ? 'patients' : 'patient/' + patientId}`;

  if (!canViewTimeTracker(currentUser.roleId)) {
    return <ErrorPage statusCode={HttpStatusCode.Forbidden} />;
  }

  return (
    <DashboardLayout breadcrumbs={editBreadcrumbs(addBreadcrumb(breadcrumbs!, newBreadcrumb), ...replacePath)}
    >
      <Helmet>
        <title>{getPageTitle(t('timeTracker.title'))}</title>
      </Helmet>
      <Container maxWidth="xl" disableGutters={true}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: matches ? 2 : 3,
          }}
        >
          <PageHeader
            headerText={t('timeTracker.title')}
            backUrl={backUrl}
          />
        </Box>
      </Container>
      <TimeTrackerFilterForm />
    </DashboardLayout>
  );
}
